import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  CreditCard,
  FolderTree,
  KeyRound,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  Package,
  Palette,
  Settings,
  ShoppingBag,
  Users,
  X,
  History,
  Receipt,
  HeartHandshake,
  PanelLeftClose,
  PanelLeftOpen,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  ChevronsUpDown,
} from 'lucide-react'
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { clearSession, getTrialStatus, loadSession, type TrialStatus } from '../lib/api'
import { Logo } from '../components/Logo'
import './shell.css'

const routePrefetchers: Record<string, () => Promise<unknown>> = {
  '/dashboard': () => import('../pages/DashboardPage'),
  '/orders': () => import('../pages/OrdersPage'),
  '/products': () => import('../pages/ProductsPage'),
  '/categories': () => import('../pages/CategoriesPage'),
  '/history': () => import('../pages/OrderHistoryPage'),
  '/staff': () => import('../pages/StaffPage'),
  '/keys': () => import('../pages/KeysPage'),
  '/theme': () => import('../pages/ThemePage'),
  '/taxes': () => import('../pages/TaxPage'),
  '/tips': () => import('../pages/TipsPage'),
  '/billing': () => import('../pages/BillingPage'),
  '/settings': () => import('../pages/SettingsPage'),
}

function prefetchRoute(path: string) {
  try {
    const fn = routePrefetchers[path]
    if (fn) {
      fn()
    }
  } catch {}
}

export function PortalContentLoader() {
  return (
    <div className="portal-loader-wrap" role="status" aria-label="Loading page content">
      <div className="portal-loader-top-indicator">
        <Loader2 size={16} className="portal-loader-spinner" />
        <span>Loading view…</span>
      </div>

      <div className="portal-skel-header">
        <div className="portal-skel-title shimmer" />
        <div className="portal-skel-desc shimmer" />
      </div>

      <div className="portal-skel-toolbar">
        <div className="portal-skel-input shimmer" />
        <div className="portal-skel-btn shimmer" />
        <div className="portal-skel-btn shimmer" />
      </div>

      <div className="portal-skel-card">
        <div className="portal-skel-table-head shimmer" />
        <div className="portal-skel-table-row shimmer" />
        <div className="portal-skel-table-row shimmer" />
        <div className="portal-skel-table-row shimmer" />
        <div className="portal-skel-table-row shimmer" />
      </div>
    </div>
  )
}

const staffNav = [
  { to: '/orders', label: 'Order Dashboard', icon: ShoppingBag },
]

const ownerNav = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/orders', label: 'Order Dashboard', icon: ShoppingBag },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/categories', label: 'Categories', icon: FolderTree },
  { to: '/history', label: 'Order History', icon: History },
  { to: '/staff', label: 'Staff', icon: Users },
  { to: '/keys', label: 'API Keys', icon: KeyRound },
  { to: '/theme', label: 'Theme', icon: Palette },
  { to: '/taxes', label: 'Taxes', icon: Receipt },
  { to: '/tips', label: 'Tips', icon: HeartHandshake },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/settings', label: 'Settings', icon: Settings },
]

export function RequireAuth({ ownerOnly = false }: { ownerOnly?: boolean }) {
  const session = loadSession()
  if (!session) return <Navigate to="/login" replace />
  const isOwner = session.role?.trim().toLowerCase() === 'owner'
  if (ownerOnly && !isOwner) {
    return <Navigate to="/orders" replace />
  }
  return <Outlet />
}

export function AppShell() {
  const session = loadSession()!
  const navigate = useNavigate()
  const location = useLocation()
  const isOwner = session?.role?.trim().toLowerCase() === 'owner'
  const [mobileOpen, setMobileOpen] = useState(false)
  const [navProgress, setNavProgress] = useState(false)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('dash_sidebar_collapsed') === 'true'
    } catch {
      return false
    }
  })
  const initials = (session?.email || 'KT').slice(0, 2).toUpperCase()

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem('dash_sidebar_collapsed', String(next))
      } catch {}
      return next
    })
  }

  // Support keyboard shortcut Ctrl+B or Cmd+B to toggle sidebar
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        toggleCollapse()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null)

  // Immediate visual feedback on route change
  useEffect(() => {
    setNavProgress(true)
    const timer = setTimeout(() => {
      setNavProgress(false)
    }, 400)
    return () => clearTimeout(timer)
  }, [location.pathname, location.search])

  // Poll or refresh trial/billing status when navigated
  useEffect(() => {
    if (!session?.accessToken) return
    getTrialStatus()
      .then((status) => setTrialStatus(status))
      .catch(() => {})
  }, [session?.accessToken, location.pathname])

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close user dropdown menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!userMenuOpen) return
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [userMenuOpen])

  // Close user dropdown when route changes
  useEffect(() => {
    setUserMenuOpen(false)
  }, [location.pathname])

  function logout() {
    clearSession()
    navigate('/', { replace: true })
  }

  const links = isOwner ? ownerNav : staffNav

  return (
    <div className={`dash${collapsed ? ' collapsed' : ''}`}>
      <aside className={`dash-sidebar${mobileOpen ? ' open' : ''}${collapsed ? ' is-collapsed' : ''}`}>
        <div className="dash-brand">
          <Logo href={isOwner ? '/dashboard' : '/orders'} size="md" markOnly={collapsed} />
          <div className="dash-brand-actions">
            <button
              type="button"
              className="dash-collapse-btn"
              title={collapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={toggleCollapse}
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            <button
              type="button"
              className="dash-close"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <nav className="dash-nav">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `dash-link${isActive ? ' active' : ''}`}
              title={collapsed ? label : undefined}
              onMouseEnter={() => prefetchRoute(to)}
              onFocus={() => prefetchRoute(to)}
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={18} className="dash-link-icon" />
              <span className="dash-link-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="dash-user" ref={userMenuRef}>
          {userMenuOpen && (
            <div className={`dash-user-menu${collapsed ? ' is-collapsed' : ''}`} role="menu" aria-label="User menu">
              <div className="dash-user-menu-header">
                <div className="dash-user-menu-avatar">{initials}</div>
                <div className="dash-user-menu-info">
                  <div className="dash-user-menu-name">{session?.displayName || session?.email}</div>
                  <div className="dash-user-menu-email">{session?.email}</div>
                  <div className="dash-user-menu-tags">
                    <span className="dash-user-role-badge">{session?.role}</span>
                    <span className="dash-user-tenant-slug">{session?.tenantSlug}</span>
                  </div>
                </div>
              </div>
              <div className="dash-user-menu-divider" />
              {isOwner && (
                <NavLink
                  to="/settings"
                  className="dash-user-menu-item"
                  onClick={() => {
                    setUserMenuOpen(false)
                    setMobileOpen(false)
                  }}
                  role="menuitem"
                >
                  <Settings size={15} />
                  <span>Account Settings</span>
                </NavLink>
              )}
              <button
                type="button"
                className="dash-user-menu-item logout"
                onClick={() => {
                  setUserMenuOpen(false)
                  logout()
                }}
                role="menuitem"
              >
                <LogOut size={15} />
                <span>Sign out</span>
              </button>
            </div>
          )}

          <button
            type="button"
            className={`dash-user-btn${userMenuOpen ? ' active' : ''}`}
            onClick={() => setUserMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={collapsed ? `${session?.displayName || session?.email} (${session?.role}) - Click for options` : 'Account menu'}
          >
            <span className="dash-avatar">{initials}</span>
            <div className="dash-user-meta">
              <p>{session?.displayName || session?.email}</p>
              <span>{session?.tenantName || session?.role}</span>
            </div>
            <ChevronsUpDown size={14} className="dash-user-chevron" />
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="dash-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="dash-main">
        {navProgress && <div className="route-progress-bar" aria-hidden="true" />}
        <header className="dash-topbar">
          <button
            type="button"
            className="dash-menu"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="dash-top-home">
            Marketing site
          </Link>
        </header>

        {trialStatus?.billingBlocked && (
          <div className="dash-billing-alert dash-billing-blocked" role="alert">
            <div className="dash-billing-alert-content">
              <AlertTriangle size={18} className="dash-alert-icon" />
              <span>
                <strong>Kiosk Ordering Paused:</strong>{' '}
                {trialStatus.billingBlockedReason === 'QUOTA_EXHAUSTED'
                  ? 'Your free trial order quota has been exhausted. Kiosks are paused until an active subscription plan is chosen.'
                  : trialStatus.billingBlockedReason === 'TRIAL_EXPIRED'
                  ? 'Your free trial period has ended. Kiosks are paused until an active subscription plan is chosen.'
                  : 'No active subscription plan found. Kiosks are currently paused.'}
              </span>
            </div>
            {isOwner && (
              <Link to="/billing" className="dash-billing-alert-action">
                Manage Subscription &amp; Plans <ArrowRight size={14} />
              </Link>
            )}
          </div>
        )}

        {!trialStatus?.billingBlocked && trialStatus?.isTrialEndingSoon && (
          <div className="dash-billing-alert dash-billing-warning" role="alert">
            <div className="dash-billing-alert-content">
              <AlertCircle size={18} className="dash-alert-icon" />
              <span>
                <strong>Trial Ending Soon:</strong>{' '}
                {trialStatus.trialOrdersRemaining !== null && trialStatus.trialOrdersRemaining <= 10
                  ? `Only ${trialStatus.trialOrdersRemaining} order${trialStatus.trialOrdersRemaining === 1 ? '' : 's'} remaining on your trial quota.`
                  : trialStatus.trialEndsAt
                  ? `Your free trial ends on ${new Date(trialStatus.trialEndsAt).toLocaleDateString()}.`
                  : 'Your free trial will expire soon.'}
              </span>
            </div>
            {isOwner && (
              <Link to="/billing" className="dash-billing-alert-action">
                Choose Plan <ArrowRight size={14} />
              </Link>
            )}
          </div>
        )}

        <main className="dash-content">
          <Suspense fallback={<PortalContentLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function Placeholder({ title, note }: { title: string; note?: string }) {
  return (
    <div>
      <PageHeader title={title} description={note} />
      <div className="empty-panel">
        <p className="empty-title">Coming next</p>
        <p className="empty-body">
          This screen is wired to the portal APIs. Full UI lands in the next build pass.
        </p>
      </div>
    </div>
  )
}
